<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import TaskCard from './TaskCard.vue';
import TaskForm from './TaskForm.vue';
import { isDone, taskSort } from './taskHelpers.js';
import { useTasksStore } from './useTasksStore.js';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  contact: {
    type: Object,
    default: null,
  },
  contactId: {
    type: [String, Number],
    default: null,
  },
  project: {
    type: Object,
    default: null,
  },
  closeCircleMemberId: {
    type: [String, Number],
    default: null,
  },
  closeCircleMemberName: {
    type: String,
    default: '',
  },
  limit: {
    type: Number,
    default: 3,
  },
  flushOnDesktop: {
    type: Boolean,
    default: true,
  },
});

const { t } = useI18n();
const tasksStore = useTasksStore();
const { state } = tasksStore;
const taskFormRef = ref(null);

const normalizedContactId = computed(
  () =>
    props.project?.contactId ??
    props.project?.customerId ??
    props.contact?.id ??
    props.contactId
);

const normalizedProjectId = computed(() => props.project?.id ?? null);

const registerContext = () => {
  if (props.contact) tasksStore.registerContact(props.contact);
  if (props.project)
    tasksStore.registerProject(props.project, normalizedContactId.value);
};

onMounted(() => {
  registerContext();
  tasksStore.loadTasks();
});
watch(() => [props.contact, props.project], registerContext, { deep: true });

const linkedTasks = computed(() => {
  let tasks = state.tasks;
  if (props.closeCircleMemberId) {
    tasks = tasks.filter(
      task =>
        String(task.linkedCloseCircleMemberId) ===
        String(props.closeCircleMemberId)
    );
  } else if (normalizedProjectId.value) {
    tasks = tasks.filter(
      task => String(task.linkedProjectId) === String(normalizedProjectId.value)
    );
  } else if (normalizedContactId.value) {
    tasks = tasks.filter(
      task => String(task.linkedContactId) === String(normalizedContactId.value)
    );
  }
  return tasks
    .slice()
    .sort((a, b) => taskSort(a, b))
    .slice(0, props.limit);
});

const pendingCount = computed(
  () => linkedTasks.value.filter(task => !isDone(task)).length
);

const openCreate = () => {
  registerContext();
  taskFormRef.value?.open({
    linkedContactId: normalizedContactId.value ?? null,
    linkedProjectId: normalizedProjectId.value ?? null,
    linkedCloseCircleMemberId: props.closeCircleMemberId ?? null,
    closeCircleMemberName: props.closeCircleMemberName,
  });
};

const completeTask = task => {
  tasksStore.completeTask(task.id);
};

const openEdit = task => {
  taskFormRef.value?.open({}, task);
};
</script>

<template>
  <section class="mt-3 flex flex-col bg-n-solid-1">
    <div
      class="flex items-center justify-between px-4 py-3"
      :class="{ 'lg:px-0': flushOnDesktop }"
    >
      <div class="flex min-w-0 flex-col gap-0.5">
        <h2 class="truncate text-sm font-semibold text-n-slate-12">
          {{ title || t('TASKS.LINKED_TITLE') }}
        </h2>
        <p class="text-xs text-n-slate-10">
          {{ t('TASKS.LINKED_COUNT', { count: pendingCount }) }}
        </p>
      </div>
      <button
        type="button"
        class="flex items-center gap-1.5 rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs font-medium text-n-slate-11 transition-colors hover:text-n-slate-12"
        @click="openCreate"
      >
        <span class="i-lucide-plus size-3.5" />
        {{ t('TASKS.CREATE_SHORT') }}
      </button>
    </div>

    <div
      v-if="linkedTasks.length"
      class="flex flex-col"
      :class="{ 'lg:-mx-4': flushOnDesktop }"
    >
      <template v-for="(task, index) in linkedTasks" :key="task.id">
        <div
          v-if="index > 0"
          class="mx-4 h-px bg-n-weak"
          :class="{ 'lg:mx-0': flushOnDesktop }"
        />
        <TaskCard
          :task="task"
          compact
          @open="openEdit"
          @complete="completeTask"
        />
      </template>
    </div>

    <div
      v-else
      class="px-4 pb-5 pt-2 text-sm text-n-slate-11"
      :class="{ 'lg:px-0': flushOnDesktop }"
    >
      <p>{{ t('TASKS.LINKED_EMPTY') }}</p>
    </div>

    <TaskForm ref="taskFormRef" />
  </section>
</template>
